import { getServicePipe } from '@pages/AdditionalServices/containers/additionServicesContext';
import { getServiceFilterAndOrderPipe } from '@pages/AdditionalServices/containers/bus/usePropertyFilterAndOrder';
import { getServicePropertyValueCachePipe } from '@pages/AdditionalServices/containers/bus/useServicePropertyValueCache';
import makeServiceData from '@pages/AdditionalServices/containers/methods/basket/makeServiceData';
import { getTabsPipe } from '@pages/AdditionalServices/containers/widgetTabsContext';
import { SHARE_SHORT_LINK_KEY } from '@pages/AdditionalServices/tabs/DeliveryForm/consts';
import { SEARCH_PARAMS } from '@pages/consts';
import { shortLinkService } from '@services/requests/shortLinkService';
import { bufferTime, distinctUntilChanged, filter, withLatestFrom } from 'rxjs';
import { deleteSearchParams, getValueFromSearchParamsByKey } from 'src/utils/search-params';

import {
  deleteExpiredSavedServiceRestore,
  removeSavedServiceRestoreByServiceId,
  setSavedServiceRestoreState,
} from '../helpers';
import { shareContext$ } from '../shareContext';
import { extractRestoreDataFromStorageSubscriber } from '../subscribers/extractRestoreDataFromStorageSubscriber';
import { checkShareAvailability } from './checkShareAvailability';
import { compareCurrentAndRestoredServicesWithoutIds } from './compareRestoredServicesWithoutIds';

/**
 * getSharePipe возвращает subscriber для возможности подписки
 * на изменение шаринга.
 */
export const getSharePipe = () => {
  return shareContext$.pipe();
};

/**
 * resetShareContext сбрасывает состояние шаринга.
 */
const resetShareContext = () => {
  shareContext$.next({
    ...shareContext$.getValue(),
    ShareData: null,
    ShareType: null,
    ShareShortLink: null,
    StorageShareData: null,
    StorageShareShortLink: null,
  });
};

/**
 * resetShare сбрасывает состояние, url параметра шаринга а также опционально
 * состояние хранилища браузера для конкретной услуги.
 */
export const resetShare = (storageServiceTabIdToDelete?: string) => {
  resetShareContext();
  deleteSearchParams([SEARCH_PARAMS.share]);
  if (storageServiceTabIdToDelete) {
    removeSavedServiceRestoreByServiceId(storageServiceTabIdToDelete);
  }
};

/**
 * Инициализация данных стейта. Загружает данные, а так же добавляет необходимую логику.
 */
export const shareInitialization = async () => {
  deleteExpiredSavedServiceRestore();
  const shortLink = getValueFromSearchParamsByKey(SEARCH_PARAMS.share);
  const shareTab = shortLink.length !== 0 ? null : getValueFromSearchParamsByKey(SEARCH_PARAMS.tab);

  try {
    const shortLinkData = await shortLinkService().LoadShortLink({ id: shortLink });

    let shareType = shortLinkData?.page;
    let shareData = JSON.parse(shortLinkData?.data ?? 'null');

    if (!shortLinkData) {
      shareType = null;
      shareData = null;
    }

    shareContext$.next({
      ...shareContext$.getValue(),
      IsShareLoading: false,
      ShareType: shareType,
      ShareData: shareData,
      ShareTab: shareTab,
    });
  } catch (e) {
    console.log(e);
  }

  // Подписка на изменения данных в табах
  // Подписка извлекает сохраненное состояние шаринга из хранилища браузера, получает данные
  // и записывает в состояние шаринга полученную короткую ссылку, данные и сбрасывает состояние флага загрузки
  getTabsPipe().subscribe(async (state) => {
    extractRestoreDataFromStorageSubscriber(state);
  });

  // Подписка на изменения данных в табах и последнего состояния сервисов.
  // Подписка очищает параметры поиска и сбрасывает состояние шары.
  getTabsPipe()
    .pipe(withLatestFrom(getServicePipe()))
    .subscribe(([, service]) => {
      if (service.IsRestoringState) {
        return;
      }

      resetShare();
    });

  // Подписка на изменение данных в состоянии сервисов. Собирает в буфер частые изменения и берет последее.
  // Забирает последние значения данных стейтов опций, фильтров и сортировки.
  // Пропускает все операции генерации, если услуги не расчитаны, пропускает выполнение если текущее и
  // восстановленное состояния одинаковы.
  // Подписка генерирует коротку ссылку шаринга и записывает в стейт.
  getServicePipe()
    .pipe(bufferTime(1000))
    .pipe(withLatestFrom(getServicePropertyValueCachePipe(), getServiceFilterAndOrderPipe()))
    .pipe(
      filter(([serviceStateBuffer, _, __]) => {
        if (serviceStateBuffer.length === 0) {
          return;
        }
        const serviceState = serviceStateBuffer[serviceStateBuffer.length - 1];

        return serviceState.LastServiceInPath?.template !== 'customsPaymentsGroup';
      })
    )
    .pipe(
      filter(
        ([
          serviceStateBuffer,
          servicePropertyValueCacheState,
          servicePropertyFilterAndOderState,
        ]) => {
          if (serviceStateBuffer.length === 0) {
            return;
          }

          const serviceState = serviceStateBuffer[serviceStateBuffer.length - 1];

          return checkShareAvailability(
            serviceState,
            servicePropertyValueCacheState,
            servicePropertyFilterAndOderState
          );
        }
      )
    )
    .pipe(
      filter(([serviceStateBuffer]) => {
        if (serviceStateBuffer.length === 0) {
          return;
        }
        const serviceState = serviceStateBuffer[serviceStateBuffer.length - 1];

        return !compareCurrentAndRestoredServicesWithoutIds(
          serviceState.SelectedVariant,
          shareContext$.getValue().ShareData
        );
      })
    )
    .subscribe(async ([serviceStateBuffer]) => {
      if (serviceStateBuffer.length === 0) {
        return;
      }
      const serviceState = serviceStateBuffer[serviceStateBuffer.length - 1];
      const serviceData = makeServiceData(serviceState.SelectedVariant);

      const generatedShortLink = await shortLinkService().GenerateShortLink({
        page: 'additional-services',
        data: JSON.stringify(serviceData),
      });

      shareContext$.next({
        ...shareContext$.getValue(),
        ShareType: 'additional-services',
        ShareData: serviceData,
        ShareShortLink: generatedShortLink,
      });
    });

  // Подписка на изменение состояния контекста табов.
  // Изменяет search параметр tab, url строки на текущий активный таб, пропускает выполнение если
  // на момент выполнения происходит восстановление. Убирает параметр tab если активна доставка
  getTabsPipe()
    .pipe(distinctUntilChanged((prev, curr) => prev.ActiveTab === curr.ActiveTab))
    .pipe(withLatestFrom(getServicePipe()))
    .subscribe(([tabs, service]) => {
      if (tabs.IsLoading) {
        return;
      }
      if (tabs.ActiveTab === 0) {
        deleteSearchParams([SEARCH_PARAMS.tab]);
        return;
      }
      if (!tabs.ActiveTabData?.data?.id || service.IsRestoringState) {
        return;
      }
      history.pushState(null, '', `?${SEARCH_PARAMS.tab}=` + tabs.ActiveTabData.data.id);
    });

  // Подписка записывает короткую ссылку шаринга в url параметры и хранилище браузера при ее наличии в стейте шаринга.
  getSharePipe()
    .pipe(withLatestFrom(getServicePipe()))
    .pipe(filter(([shareState]) => !!shareState.ShareShortLink))
    .subscribe(([shareState, serviceState]) => {
      const rootService = serviceState.ServicesPath[0];

      setSavedServiceRestoreState(rootService.id, shareState.ShareShortLink);
      history.pushState(null, '', `?${SHARE_SHORT_LINK_KEY}=` + shareState.ShareShortLink);
    });
};
